import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/formatters';
import * as XLSX from 'xlsx';

const FONT = "'Outfit', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const Icon = ({ d, size = 20, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ICONS = {
    revenue: ['M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    expense: ['M22 12h-4l-3 9L9 3l-3 9H2'],
    pending: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    profit: 'M23 6l-9.5 9.5-5-5L1 18',
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
};

/* ─── Donut Chart ─── */
function DonutChart({ data, total, size = 180, formatter }) {
    const sum = data.reduce((s, d) => s + d.value, 0);
    const stroke = Math.round(size * 0.17);
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const inner = size - stroke * 2;
    const label = formatter ? formatter(total ?? sum) : String(total ?? sum);
    // shrink the centre text so long amounts always fit inside the hole
    const fontSize = Math.max(10, Math.min(17, (inner * 1.55) / Math.max(label.length, 1)));

    let offset = 0;
    const slices = sum > 0 ? data.filter(d => d.value > 0).map(d => {
        const len = (d.value / sum) * c;
        const slice = { ...d, len, offset };
        offset += len;
        return slice;
    }) : [];

    return (
        <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
                {slices.map((s, i) => (
                    <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
                        stroke={s.color} strokeWidth={stroke}
                        strokeDasharray={`${s.len} ${c - s.len}`}
                        strokeDashoffset={-s.offset}>
                        <title>{`${s.label}: ${formatter ? formatter(s.value) : s.value}`}</title>
                    </circle>
                ))}
            </svg>
            <div style={{ position: 'absolute', inset: stroke, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', pointerEvents: 'none' }}>
                {sum > 0 ? (
                    <>
                        <div style={{ fontSize, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, whiteSpace: 'nowrap' }}>{label}</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1.5, marginTop: 4 }}>TOTAL</div>
                    </>
                ) : (
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>No expenses</div>
                )}
            </div>
        </div>
    );
}

/* ─── Ranked bar list (Customer Revenue) ─── */
function RankedBars({ data, total, formatter }) {
    if (!data || data.length === 0) {
        return <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>No invoices found for this period</div>;
    }
    const maxVal = Math.max(...data.map(d => d.value), 1);
    return (
        <div className="fd-rank">
            {data.map((d, i) => {
                const pct = total > 0 ? (d.value / total) * 100 : 0;
                return (
                    <div key={d.id || i} className="fd-rank-row">
                        <div className="fd-rank-name" title={d.label}>{d.label}</div>
                        <div className="fd-rank-track">
                            <div style={{ width: `${Math.max((d.value / maxVal) * 100, d.value > 0 ? 1.5 : 0)}%`, height: '100%', background: d.color, borderRadius: 6, transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
                        </div>
                        <div className="fd-rank-value">
                            <span>{formatter ? formatter(d.value) : d.value}</span>
                            <span className="fd-rank-pct">{pct.toFixed(1)}%</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

const RESPONSIVE_CSS = `
.fd-root { padding: 28px 36px; }
.fd-hero { padding: 32px 40px; }
.fd-hero-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.fd-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px; margin-bottom: 26px; }
.fd-kpi-value { font-size: clamp(20px, 2vw, 28px); overflow-wrap: anywhere; }
.fd-charts { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 20px; align-items: stretch; }
.fd-card { background: white; border-radius: 18px; padding: 26px 30px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.04); min-width: 0; container-type: inline-size; }

.fd-rank { display: grid; grid-template-columns: minmax(90px, 170px) minmax(0, 1fr) auto; align-items: center; column-gap: 14px; row-gap: 14px; padding-top: 4px; }
.fd-rank-row { display: contents; }
.fd-rank-name { font-size: 13px; font-weight: 600; color: #334155; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fd-rank-track { height: 26px; background: #f1f5f9; border-radius: 6px; overflow: hidden; }
.fd-rank-value { font-size: 13px; font-weight: 800; color: #0f172a; white-space: nowrap; text-align: right; display: flex; gap: 8px; align-items: baseline; justify-content: flex-end; }
.fd-rank-pct { font-size: 11px; font-weight: 700; color: #94a3b8; min-width: 40px; }

.fd-exp-body { display: flex; align-items: center; gap: 28px; }
.fd-exp-legend { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 12px; }
.fd-exp-row { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; align-items: center; gap: 10px; }
.fd-exp-label { display: flex; align-items: center; gap: 8px; min-width: 0; }
.fd-exp-label span { font-size: 13px; font-weight: 600; color: #475569; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fd-exp-amt { font-size: 13px; font-weight: 800; color: #0f172a; white-space: nowrap; text-align: right; }
.fd-exp-pct { font-size: 11px; font-weight: 700; color: #94a3b8; white-space: nowrap; min-width: 40px; text-align: right; }

@container (max-width: 460px) {
  .fd-exp-body { flex-direction: column; align-items: stretch; }
  .fd-exp-body > :first-child { align-self: center; }
  .fd-rank { display: flex; flex-direction: column; gap: 14px; }
  .fd-rank-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; column-gap: 10px; row-gap: 6px; }
  .fd-rank-track { grid-column: 1 / -1; grid-row: 2; height: 18px; }
}

@media (max-width: 1280px) { .fd-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1100px) { .fd-charts { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 640px) {
  .fd-root { padding: 16px; }
  .fd-hero { padding: 22px 20px; }
  .fd-kpis { grid-template-columns: minmax(0, 1fr); gap: 12px; }
  .fd-card { padding: 20px 18px; }
}
`;

const EXPENSE_COLORS = {
    Fuel: '#1e3a8a',
    Maintenance: '#8b5cf6',
    Payroll: '#3b82f6',
    'Spare Parts': '#0d9488',
    General: '#f59e0b',
};

export default function FinancialDashboard({ user, operatorDetails }) {
    const [summary, setSummary] = useState(null);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);

    useEffect(() => {
        if (!operatorId) return;
        setLoading(true);
        Promise.all([
            fetch(`/api/financial-summary/${operatorId}`).then(res => res.json()),
            fetch(`/api/countries`).then(res => res.json())
        ])
        .then(([summaryData, countriesData]) => {
            setSummary(summaryData);
            setCountries(countriesData);
        })
        .catch(err => console.error('Error fetching financial dashboard data:', err))
        .finally(() => setLoading(false));
    }, [operatorId]);

    const formatAmount = (amount) => {
        const cName = operatorDetails?.country;
        const cData = countries.find(c => c.name === cName);
        return formatCurrency(amount, cName, cData);
    };

    const revenue = summary?.totalRevenue || 0;
    const expenses = summary?.totalExpenses || 0;
    const pendingInvoices = (summary?.totalRevenue || 0) - (summary?.paidRevenue || 0);
    const netProfit = summary?.netProfit || 0;

    const cards = [
        { label: 'Total Revenue', value: revenue, icon: ICONS.revenue, color: '#2563eb', bg: '#eff6ff' },
        { label: 'Total Expenses', value: expenses, icon: ICONS.expense, color: '#1d4ed8', bg: '#eff6ff' },
        { label: 'Pending Invoices', value: pendingInvoices, icon: ICONS.pending, color: '#0284c7', bg: '#f0f9ff' },
        { label: 'Net Profit', value: netProfit, icon: ICONS.profit, color: '#1e40af', bg: '#eff6ff' },
    ];

    // Real revenue per customer from the API: top 5, the rest grouped as "Others"
    const allClients = (summary?.revenueByClient || []).filter(c => c.revenue > 0);
    const TOP_N = 5;
    const topClients = allClients.slice(0, TOP_N).map(c => ({ id: c.id, label: c.name, value: c.revenue, color: '#2563eb' }));
    const othersTotal = allClients.slice(TOP_N).reduce((s, c) => s + c.revenue, 0);
    const customerData = othersTotal > 0
        ? [...topClients, { id: 'others', label: `Others (${allClients.length - TOP_N})`, value: othersTotal, color: '#94a3b8' }]
        : topClients;

    // Real expense categories only — no estimated fallbacks
    const expenseData = [
        { label: 'Fuel', value: summary?.totalFuelExpenses || 0 },
        { label: 'Maintenance', value: summary?.totalMaintenanceExpenses || 0 },
        { label: 'Payroll', value: summary?.totalPayrollOverhead || 0 },
        { label: 'Spare Parts', value: summary?.totalSparePartsExpenses || 0 },
        { label: 'General', value: summary?.totalGeneralExpenses || 0 },
    ].map(d => ({ ...d, color: EXPENSE_COLORS[d.label] }))
     .sort((x, y) => y.value - x.value);

    const exportToExcel = () => {
        const wb = XLSX.utils.book_new();
        
        const overviewData = [
            ['Metric', 'Amount'],
            ['Total Revenue', revenue],
            ['Total Expenses', expenses],
            ['Pending Invoices', pendingInvoices],
            ['Net Profit', netProfit]
        ];
        const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
        XLSX.utils.book_append_sheet(wb, overviewSheet, 'Financial Overview');

        const customerSheetData = [['Client', 'Invoices', 'Revenue', 'Share %']];
        allClients.forEach(c => customerSheetData.push([c.name, c.invoiceCount, c.revenue, revenue > 0 ? +((c.revenue / revenue) * 100).toFixed(2) : 0]));
        const customerSheet = XLSX.utils.aoa_to_sheet(customerSheetData);
        XLSX.utils.book_append_sheet(wb, customerSheet, 'Customer Revenue');

        const expensesSheetData = [['Category', 'Amount', 'Share %']];
        expenseData.forEach(d => expensesSheetData.push([d.label, d.value, expenses > 0 ? +((d.value / expenses) * 100).toFixed(2) : 0]));
        const expensesSheet = XLSX.utils.aoa_to_sheet(expensesSheetData);
        XLSX.utils.book_append_sheet(wb, expensesSheet, 'Expense Breakdown');

        XLSX.writeFile(wb, 'Financial_Dashboard_Export.xlsx');
    };

    return (
        <div className="fd-root" style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: FONT }}>
            <style>{RESPONSIVE_CSS}</style>
            {/* Deep Navy Blue Hero Header */}
            <div className="fd-hero" style={{
                background: 'linear-gradient(135deg, #0b1e3b 0%, #112d59 52%, #183d73 100%)',
                borderRadius: 22,
                marginBottom: 26,
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 20px 40px -8px rgba(11,30,59,0.45)'
            }}>
                <div style={{ position: 'absolute', top: -70, right: -40, width: 340, height: 340, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.22) 0%, transparent 65%)', pointerEvents: 'none' }} />
                <div className="fd-hero-row" style={{ position: 'relative', zIndex: 2 }}>
                    <div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 9999, background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', color: '#93c5fd', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', border: '1px solid rgba(255,255,255,0.15)', marginBottom: 10 }}>
                            Financial Analytics
                        </div>
                        <h1 style={{ fontSize: 30, fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.03em' }}>Financial Dashboard</h1>
                        <p style={{ color: '#cbd5e1', fontSize: 14, margin: '6px 0 0', fontWeight: 500 }}>Overview of revenue, expenses, and profitability</p>
                    </div>
                    <button 
                        onClick={exportToExcel}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '12px 22px', background: '#2563eb', color: 'white',
                            border: '1px solid #3b82f6', borderRadius: 12, fontSize: 13.5, fontWeight: 700,
                            cursor: 'pointer', transition: 'all 0.2s ease',
                            fontFamily: FONT, boxShadow: '0 4px 14px rgba(37,99,235,0.3)'
                        }}
                    >
                        <Icon d={ICONS.download} size={16} color="white" />
                        Export Report
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="fd-kpis">
                {cards.map((c, i) => (
                    <div key={c.label} style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: `1px solid #e2e8f0`, borderTop: `4px solid ${c.color}`, boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {c.label}
                            </div>
                            <div style={{ width: 38, height: 38, borderRadius: 10, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={c.icon} size={18} color={c.color} />
                            </div>
                        </div>
                        <div className="fd-kpi-value" style={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                            {loading ? '...' : formatAmount(c.value)}
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Row */}
            <div className="fd-charts">
                {/* Revenue by Customer */}
                <div className="fd-card">
                    <div style={{ marginBottom: 20 }}>
                        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>Highest Revenue by Customer</h2>
                        <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Top contributing clients to overall revenue (invoiced amount).</p>
                    </div>
                    {loading ? (
                        <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Loading...</div>
                    ) : (
                        <RankedBars data={customerData} total={revenue} formatter={formatAmount} />
                    )}
                </div>

                {/* Expense Categories */}
                <div className="fd-card">
                    <div style={{ marginBottom: 20 }}>
                        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>Expense Breakdown</h2>
                        <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Distribution of costs across categories.</p>
                    </div>
                    {loading ? (
                        <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Loading...</div>
                    ) : (
                        <div className="fd-exp-body">
                            <DonutChart data={expenseData} total={expenses} size={180} formatter={formatAmount} />
                            <div className="fd-exp-legend">
                                {expenseData.map((d) => (
                                    <div key={d.label} className="fd-exp-row">
                                        <div className="fd-exp-label">
                                            <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                                            <span>{d.label}</span>
                                        </div>
                                        <span className="fd-exp-amt">{formatAmount(d.value)}</span>
                                        <span className="fd-exp-pct">{expenses > 0 ? ((d.value / expenses) * 100).toFixed(1) : '0.0'}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
